import {
  CaretDownIcon,
  CaretUpIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import type React from "react";
import {
  type ChangeEvent,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTriggerWithActions,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import FilterSearchBar from "@/components/ui/molecules/FilterSearchBar";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type { DynamicFilterItem } from "@/store/news-feed/filters/dynamic-filters.slice";
import FilterComponentItem from "./FilterComponentItem";

/**
 * Filter data structure for dynamic filters
 */
export type DynamicFilterData = {
  items: DynamicFilterItem[];
  isLoading: boolean;
  error: string | null;
};

interface NewsFeedFilterSectionWithSearchProps {
  title: string;
  category: ShareVoiceSidebarCategory;
  filterData: DynamicFilterData;
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
  onToggleFilter: (category: ShareVoiceSidebarCategory, item: string) => void;
  /** Number of items to show initially (default: 10) */
  visibleCount?: number;
  /** Action items to render in the accordion header (e.g., buttons, menus) */
  actions?: React.ReactNode;
  /** Function to render action menu for each filter item */
  renderItemActions?: (item: DynamicFilterItem) => React.ReactNode;
  /** Whether the accordion is expanded */
  isExpanded?: boolean;
  /** Callback when accordion expansion changes */
  onValueChange?: (value: string) => void;
  /** Whether to show the count in the header (default: true) */
  showCount?: boolean;
  /** Control text truncation vs wrapping for filter item labels */
  labelTextWrap?: "truncate" | "wrap";
  /** Message shown when there are no items and no active search term */
  emptyStateMessage?: string;
}

/**
 * Memoized filter section component with search functionality for dynamic news feed filters
 * Handles loading, error states, search filtering, and renders dynamic filter items
 */
const NewsFeedFilterSectionWithSearch = ({
  title,
  category,
  filterData,
  selectedFilters,
  onToggleFilter,
  visibleCount: initialVisibleCount = 10,
  actions,
  renderItemActions,
  isExpanded = false,
  onValueChange,
  showCount = true,
  labelTextWrap,
  emptyStateMessage,
}: NewsFeedFilterSectionWithSearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [showError, setShowError] = useState(false);
  const titleRef = useRef(title);

  // Keep title ref up to date without triggering effect re-runs
  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return filterData.items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return filterData.items.filter((item) =>
      item.label.toLowerCase().includes(lowerSearchTerm),
    );
  }, [filterData.items, searchTerm]);

  const visibleItems = useMemo(() => {
    return filteredItems.slice(0, visibleCount);
  }, [filteredItems, visibleCount]);

  const hasMoreItems = visibleCount < filteredItems.length;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
    setVisibleCount(initialVisibleCount); // Reset to initial count when searching
  };

  const handleClearSearch = (): void => {
    setSearchTerm("");
    setVisibleCount(initialVisibleCount); // Reset to initial count when clearing search
  };

  const handleShowMore = (): void => {
    setVisibleCount((prev) =>
      Math.min(prev + initialVisibleCount, filteredItems.length),
    );
  };

  const handleCollapse = (): void => {
    setVisibleCount(initialVisibleCount);
  };

  const showSearch = filterData.items.length > initialVisibleCount;

  // Show skeleton and header spinner for every fetch (initial and refetch)
  const showSkeleton = filterData.isLoading;

  // Determine if we've fetched data (have items, error, or are loading)
  // This helps us avoid showing "(0)" when data hasn't been fetched yet
  const hasFetchedData =
    filterData.items.length > 0 ||
    filterData.error !== null ||
    filterData.isLoading;

  // Reset visible count when accordion is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setVisibleCount(initialVisibleCount);
    }
  }, [isExpanded, initialVisibleCount]);

  // Auto-dismiss error message after 15 seconds
  useEffect(() => {
    if (!filterData.error) {
      setShowError(false);
      return;
    }

    console.error(`${titleRef.current} error:`, filterData.error);
    setShowError(true);

    const timer = setTimeout(() => {
      setShowError(false);
    }, 15000);

    return () => {
      clearTimeout(timer);
    };
  }, [filterData.error]);

  // Button classes for both show more and show less buttons
  const buttonClasses =
    "text-[10px] font-medium text-gray-500 flex items-center gap-1 px-0 h-8 hover:text-gray-700 underline-offset-4 underline";

  const renderContent = () => {
    if (showSkeleton) {
      return <ItemSkeleton showNestedItems={false} />;
    }

    return (
      <>
        {showSearch && (
          <FilterSearchBar
            searchTerm={searchTerm}
            title={title}
            onSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
            isOpen={isExpanded}
          />
        )}

        {filterData.error && showError && (
          <div className="text-xxs text-red-500 leading-snug py-0.5">
            An error occurred while fetching the filters. Please try again
            later.
          </div>
        )}

        {filteredItems.length === 0 && searchTerm.trim() && (
          <div className="text-xs text-gray-500 py-2">
            No filters found matching for "{searchTerm}"
          </div>
        )}

        {filterData.items.length === 0 &&
          !searchTerm.trim() &&
          emptyStateMessage && (
            <div className="text-xs text-gray-500 py-2">
              {emptyStateMessage}
            </div>
          )}

        {visibleItems.map((item, index) => {
          // Use the item's category if available, otherwise fall back to the prop category
          const itemCategory = item.category
            ? (item.category as ShareVoiceSidebarCategory)
            : category;

          return (
            <FilterComponentItem
              key={item.id}
              title={item.label}
              count={item.total}
              selected={
                selectedFilters.get(itemCategory)?.includes(item.id) ?? false
              }
              tabIndex={index}
              toggleFilter={() => onToggleFilter(itemCategory, item.id)}
              className="px-0"
              labelTextWrap={labelTextWrap}
            >
              {renderItemActions?.(item)}
            </FilterComponentItem>
          );
        })}

        {hasMoreItems && (
          <Button
            variant="none"
            size="sm"
            onPress={handleShowMore}
            className={buttonClasses}
          >
            <CaretDownIcon size={12} />
            Show More ({filteredItems.length - visibleCount} remaining)
          </Button>
        )}

        {!hasMoreItems && visibleCount > initialVisibleCount && (
          <Button
            variant="none"
            size="sm"
            onPress={handleCollapse}
            className={buttonClasses}
          >
            <CaretUpIcon size={12} />
            Show Less
          </Button>
        )}
      </>
    );
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? category : ""}
      onValueChange={onValueChange}
    >
      <AccordionItem value={category} className="px-2">
        <AccordionTriggerWithActions
          size="sm"
          className="font-medium text-xs text-gray-700 py-2"
          actions={actions}
        >
          <div className="text-xs flex items-center gap-1">
            <span className="text-gray-700 font-medium">{title}</span>
            {showCount &&
              (showSkeleton ? (
                <SpinnerGapIcon size={12} className="animate-spin flex" />
              ) : hasFetchedData ? (
                <span className="text-gray-500 font-normal">
                  {` (${filteredItems.length})`}
                </span>
              ) : null)}
          </div>
        </AccordionTriggerWithActions>
        <AccordionContent className="pb-2">{renderContent()}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

NewsFeedFilterSectionWithSearch.displayName = "NewsFeedFilterSectionWithSearch";

export default memo(NewsFeedFilterSectionWithSearch);
